import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { type Location, useGetOrganisationLocations } from '../../../../app/ApiGen';
import { RefreshButton } from '../../../../components/DataTable/Toolbar';
import { DataErrorHandler } from '../../../../components/ErrorHandler';
import { ExportCsv, type ExportProps } from '../../../../components/ExportCsv';
import { NoLocations } from '../../../../components/NoContentPlaceholder/NoLocations';
import {
  ColumnChanger,
  type ColumnChangerProps,
  DataTable,
  SearchField,
  SearchFieldProps,
  Section,
  Workspace,
} from '../../../../design-system';
import { fullStateSelector } from '../../../../store/root';
import { navigateOnRowClick } from '../../../../utils/dataTable/rowHelpers';
import { tableColumns } from '../../../../utils/locations/table';
import { normalise, type Normalised } from '../../../../utils/request';

type TableLocation = Pick<Normalised<Location>, 'address' | 'icpNumber' | 'id' | 'name'> & {
  chargePointCount: number;
  gridExitPoint?: string;
  electricityDistributor?: string;
  electricityRetailer?: string;
};

const getTableLocations = (locations: Normalised<Location>[]): TableLocation[] =>
  locations.map((location) => {
    const { address, chargePoints, icpNumber, icpDetails, id, name } = location;
    return {
      address,
      chargePointCount: chargePoints.length,
      icpNumber,
      gridExitPoint: icpDetails?.networkConnectionPoint,
      electricityDistributor: icpDetails?.electricityDistributor,
      electricityRetailer: icpDetails?.electricityRetailer,
      id,
      name,
    };
  });

const LocationListView: React.FC = () => {
  const { url, params } = useRouteMatch<{ slug: string }>();
  const history = useHistory();
  const { isAdmin, isDriver, isEvnexAdmin } = useSelector(fullStateSelector);

  const orgSlug: string = params.slug;
  const { loading, error, data, refetch } = useGetOrganisationLocations({ orgSlug });

  const tableLocations = React.useMemo<TableLocation[]>(() => (data ? getTableLocations(normalise(data)) : []), [data]);
  const columns = React.useMemo(
    () => tableColumns<TableLocation>({ getUrl: (location: TableLocation) => `${url}/${location.id}` }),
    [url],
  );

  if (error) {
    return <DataErrorHandler description="Unable to load locations" error={error} refetch={refetch} />;
  }

  if (!loading && tableLocations.length === 0) {
    return (
      <Workspace maxWidth="xl">
        <Section>
          <Card>
            <Grid style={{ padding: 30 }} container direction="column" justifyContent="center" alignItems="center">
              <NoLocations orgSlug={orgSlug} isDriver={isDriver} />
            </Grid>
          </Card>
        </Section>
      </Workspace>
    );
  }

  return (
    <Workspace maxWidth="xl">
      <Card>
        <DataTable<TableLocation>
          isLoading={loading}
          options={{ tableLayout: 'auto', columnsButton: true, loadingType: 'linear' }}
          toolbarProps={{
            search: ({ dataManager, search, onSearchChanged }: SearchFieldProps) => (
              <SearchField
                dataManager={dataManager}
                item="locations"
                onSearchChanged={onSearchChanged}
                search={search}
              />
            ),
            actions: (props: React.PropsWithChildren<ColumnChangerProps> & ExportProps<TableLocation>) => (
              <>
                <ColumnChanger
                  columnsButton={props.columnsButton}
                  columns={props.columns}
                  icons={props.icons}
                  onColumnsChanged={props.onColumnsChanged}
                />
                <RefreshButton refetch={refetch} isLoading={loading} />
                <ExportCsv<TableLocation>
                  columns={props.columns}
                  data={props.data}
                  exportFileName={`organisation-${orgSlug}-locations`}
                  getFieldValue={props.getFieldValue}
                  icons={props.icons}
                />
                {(isAdmin || isEvnexAdmin) && (
                  <Button
                    color="primary"
                    component={Link}
                    to={`/organisations/${orgSlug}/create/location`}
                    variant="contained"
                  >
                    Add location
                  </Button>
                )}
              </>
            ),
          }}
          columns={[
            columns.name,
            columns.address,
            columns.chargePointCount,
            columns.icpNumber,
            columns.gridExitPoint,
            columns.electricityDistributor,
            columns.electricityRetailer,
          ]}
          data={tableLocations}
          onRowClick={navigateOnRowClick((rowData) => `${url}/${rowData.id}`, history)}
        />
      </Card>
    </Workspace>
  );
};

export default LocationListView;
